import { useEffect, useMemo, useState } from "react";
import { joinWibPartsToIso, splitIsoToWibParts } from "@/lib/time";

/**
 * Admin-only "TGL SELESAI" picker.
 *
 * Two independent controls — a native `<input type="date">` and a masked
 * `HH:mm` text input backed by 24-hour hour/minute number inputs. This
 * side-steps the native `<input type="datetime-local">` which:
 *   - Renders AM/PM on en-US locale browsers regardless of `lang` hint.
 *   - Interprets the value in the *browser* timezone, which silently
 *     shifts the day for containers/servers that default to UTC.
 *
 * Storage contract:
 *   value    : ISO 8601 string in UTC (or null)
 *   onChange : callback with an ISO 8601 string carrying "+07:00" so the
 *              server receives an unambiguous WIB moment and stores UTC.
 */
export default function WibDateTimePicker({
  value, onChange, disabled = false, testidPrefix = "tf-completed-at",
}) {
  // Local mirror keeps the string form the user is typing decoupled from
  // the ISO parent value; parent is only updated when both halves are
  // valid, so half-typed times don't emit spurious nulls.
  const [d, setD] = useState("");
  const [t, setT] = useState("");

  useEffect(() => {
    const parts = splitIsoToWibParts(value);
    setD(parts.date);
    setT(parts.time);
  }, [value]);

  const timeInvalid = useMemo(() => {
    if (!t) return false;
    return !/^([01]\d|2[0-3]):[0-5]\d$/.test(t);
  }, [t]);

  const emit = (nextDate, nextTime) => {
    if (!nextDate && !nextTime) return onChange(null);
    const iso = joinWibPartsToIso(nextDate, nextTime);
    // Only propagate when both halves are complete; intermediate typing
    // states (e.g. "08:" while the user reaches for the minute) don't wipe
    // the parent value.
    if (iso) onChange(iso);
  };

  const clear = () => {
    setD(""); setT("");
    onChange(null);
  };

  const inputCls =
    "h-9 rounded-md border border-[#E5E7EB] bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0055FF]";

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
        <input
          type="date"
          value={d}
          onChange={(e) => { setD(e.target.value); emit(e.target.value, t); }}
          disabled={disabled}
          className={`${inputCls} w-full`}
          data-testid={`${testidPrefix}-date`}
          aria-label="Tanggal selesai (WIB)"
          lang="id-ID"
        />
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-2][0-9]:[0-5][0-9]"
          placeholder="HH:mm"
          value={t}
          maxLength={5}
          onChange={(e) => {
            // Sanitize: keep digits + a single colon. Auto-insert the colon
            // once the user has typed two digits so `0810` becomes `08:10`
            // while they type. Handles paste and playwright-style rapid
            // typing by collapsing duplicate colons.
            let raw = e.target.value.replace(/[^0-9:]/g, "").replace(/:+/g, ":");
            if (raw.length === 2 && !raw.includes(":") && t.length < raw.length) {
              raw = raw + ":";
            }
            // Cap at 5 chars (HH:mm) — the input's maxLength=5 is a fallback
            // for paste, this ensures typing never grows past.
            if (raw.length > 5) raw = raw.slice(0, 5);
            setT(raw);
            if (/^([01]\d|2[0-3]):[0-5]\d$/.test(raw)) emit(d, raw);
          }}
          disabled={disabled}
          className={`${inputCls} w-[80px] font-mono tabular-nums text-center ${timeInvalid ? "border-[#DC2626]" : ""}`}
          data-testid={`${testidPrefix}-time`}
          aria-label="Jam selesai 24 jam (WIB)"
        />
        <button
          type="button"
          onClick={clear}
          disabled={disabled || (!d && !t)}
          className="h-9 px-2 rounded-md border border-[#E5E7EB] text-[10px] font-bold uppercase tracking-widest text-[#6B7280] hover:bg-[#F8F9FA] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testidPrefix}-clear`}
          aria-label="Kosongkan TGL SELESAI"
          title="Kosongkan"
        >
          Kosongkan
        </button>
      </div>
      <div className="text-[10px] text-[#9CA3AF]">
        Format 24 jam WIB (Asia/Jakarta). Contoh: 08:10, 14:30, 23:45. Kosongkan agar server mengisi otomatis saat status DONE.
      </div>
      {timeInvalid && (
        <div className="text-[11px] text-[#DC2626]" data-testid={`${testidPrefix}-error`}>
          Format jam tidak valid. Gunakan HH:mm (00-23:00-59).
        </div>
      )}
    </div>
  );
}
